import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import type { RewardBudget, RewardMerchant } from '@/api/types';

export const rewardsKeys = {
  all: ['rewards'] as const,
  merchants: () => [...rewardsKeys.all, 'merchants'] as const,
  merchantList: (pageSize: number, pageToken: string, filter: string) =>
    [...rewardsKeys.merchants(), 'list', pageSize, pageToken, filter] as const,
  merchant: (id: string) => [...rewardsKeys.merchants(), 'detail', id] as const,
  merchantRevisions: (id: string, pageToken: string) => [...rewardsKeys.merchants(), 'revisions', id, pageToken] as const,
  budgets: () => [...rewardsKeys.all, 'budgets'] as const,
  budgetList: (pageSize: number, pageToken: string) => [...rewardsKeys.budgets(), 'list', pageSize, pageToken] as const,
  budget: (id: string) => [...rewardsKeys.budgets(), 'detail', id] as const,
  payments: (filter: string, pageSize: number, pageToken: string) =>
    [...rewardsKeys.all, 'payments', filter, pageSize, pageToken] as const,
  programme: () => [...rewardsKeys.all, 'programme'] as const,
};

/** A page of partners; `filter` is AIP-160 over `state`, `category` and `live`, empty for every one. */
export function useRewardMerchants(pageSize: number, pageToken: string, filter = '') {
  return useQuery({
    queryKey: rewardsKeys.merchantList(pageSize, pageToken, filter),
    queryFn: () =>
      adminClient.listRewardMerchants({ pageSize, pageToken: pageToken || undefined, filter: filter || undefined }),
  });
}

export function useRewardMerchant(id: string) {
  return useQuery({
    queryKey: rewardsKeys.merchant(id),
    queryFn: () => adminClient.getRewardMerchant(id),
    enabled: id !== '',
  });
}

export function useRewardMerchantRevisions(id: string, pageToken: string) {
  return useQuery({
    queryKey: rewardsKeys.merchantRevisions(id, pageToken),
    queryFn: () => adminClient.listRewardMerchantRevisions(id, { pageToken: pageToken || undefined }),
  });
}

export function useRewardBudgets(pageSize: number, pageToken: string) {
  return useQuery({
    queryKey: rewardsKeys.budgetList(pageSize, pageToken),
    queryFn: () => adminClient.listRewardBudgets({ pageSize, pageToken: pageToken || undefined }),
  });
}

export function useRewardBudget(id: string) {
  return useQuery({
    queryKey: rewardsKeys.budget(id),
    queryFn: () => adminClient.getRewardBudget(id),
    enabled: id !== '',
  });
}

export function useRewardPayments(filter: string, pageSize: number, pageToken: string) {
  return useQuery({
    queryKey: rewardsKeys.payments(filter, pageSize, pageToken),
    queryFn: () => adminClient.listRewardPayments({ filter: filter || undefined, pageSize, pageToken: pageToken || undefined }),
  });
}

/** The money picture. Balances move under the worker, so it is never served stale. */
export function useRewardProgramme() {
  return useQuery({
    queryKey: rewardsKeys.programme(),
    queryFn: () => adminClient.queryRewardProgramme(),
    staleTime: 0,
  });
}

// Every change can move a list, a detail and a revision history at once, so
// a success invalidates the whole collection. A change to one resource also
// writes the server's answer into its detail first: the page then shows the
// new state and etag at once, so no action is offered from the old state
// while the refetch is in flight.

export function useCreateRewardMerchant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { merchant: Omit<RewardMerchant, 'name'>; requestId: string }) =>
      adminClient.createRewardMerchant(params.merchant, params.requestId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rewardsKeys.merchants() }),
  });
}

export function useUpdateRewardMerchant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; merchant: RewardMerchant; updateMask: readonly string[]; requestId: string }) =>
      adminClient.updateRewardMerchant(params.id, params.merchant, params.updateMask, params.requestId),
    onSuccess: (saved, params) => {
      queryClient.setQueryData(rewardsKeys.merchant(params.id), saved);
      return queryClient.invalidateQueries({ queryKey: rewardsKeys.merchants() });
    },
  });
}

export function useChangeRewardMerchantState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; change: 'pause' | 'resume' | 'retire'; etag: string; reason: string; requestId: string }) =>
      adminClient.changeRewardMerchantState(params.id, params.change, {
        etag: params.etag,
        reason: params.reason,
        requestId: params.requestId,
      }),
    onSuccess: (saved, params) => {
      queryClient.setQueryData(rewardsKeys.merchant(params.id), saved);
      return queryClient.invalidateQueries({ queryKey: rewardsKeys.merchants() });
    },
  });
}

/** Reads a Google Maps link's place. It changes nothing, so nothing is invalidated. */
export function useResolveLocationLink() {
  return useMutation({
    mutationFn: (uri: string) => adminClient.resolveRewardMerchantLocationLink(uri),
  });
}

export function useCreateRewardBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { budget: Omit<RewardBudget, 'name'>; requestId: string }) =>
      adminClient.createRewardBudget(params.budget, params.requestId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rewardsKeys.budgets() }),
  });
}

export function useUpdateRewardBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; budget: RewardBudget; updateMask: readonly string[]; requestId: string }) =>
      adminClient.updateRewardBudget(params.id, params.budget, params.updateMask, params.requestId),
    onSuccess: (saved, params) => {
      queryClient.setQueryData(rewardsKeys.budget(params.id), saved);
      return queryClient.invalidateQueries({ queryKey: rewardsKeys.budgets() });
    },
  });
}

export function useChangeRewardBudgetState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; change: 'pause' | 'resume' | 'close'; etag: string; requestId: string }) =>
      adminClient.changeRewardBudgetState(params.id, params.change, { etag: params.etag, requestId: params.requestId }),
    onSuccess: (saved, params) => {
      queryClient.setQueryData(rewardsKeys.budget(params.id), saved);
      return queryClient.invalidateQueries({ queryKey: rewardsKeys.budgets() });
    },
  });
}
